import React, { FC, useState } from 'react';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { IHTMLPopUpTemplateFormData } from '../../types';
import TextInput from 'components/TextInput/TextInput';
import HTMLPopUpPreview from './HTMLPopUpTemplatePreview/HTMLPopUpTemplatePreview';

interface AccordionProps {
  title: string;
  section: string;
  level?: 'h3' | 'h4' | 'h5' | 'h6';
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const Accordion: FC<AccordionProps> = ({ title, section, level = 'h4', children, isOpen, onToggle }) => {
  const HeaderTag = level;
  return (
    <div className={`mb-4 overflow-hidden border-gray-200  rounded shadow-md ${isOpen ? '' : 'hover:bg-gray-50'}`}>
      <HeaderTag
        className={`flex items-center m-0 justify-between px-2 py-2 cursor-pointer text-muted-dark-purple hover:bg-gray-50 ${
          isOpen ? 'rounded-t' : 'rounded'
        }`}
        onClick={onToggle}
      >
        {title}
        {isOpen ? <ArrowDropUp /> : <ArrowDropDown />}
      </HeaderTag>
      {isOpen && <div className="p-4 bg-white">{children}</div>}
    </div>
  );
};

interface IHTMLPopUpTemplateFields {
  htmlPopUpTemplateFormData: IHTMLPopUpTemplateFormData;
  setHTMLPopUpTemplateFormData: (data: (prev: IHTMLPopUpTemplateFormData) => IHTMLPopUpTemplateFormData) => void;
  isEditingDisabled?: boolean;
}

const HTMLPopUpTemplateFields: FC<IHTMLPopUpTemplateFields> = ({
  htmlPopUpTemplateFormData,
  setHTMLPopUpTemplateFormData,
  isEditingDisabled = false,
}) => {
  const [accordionState, setAccordionState] = useState({
    text_and_background_image_content: false,
    merchant_logo_content: false,
    button_content: false,
    container_background_image_style: false,
    merchant_logo_style: false,
    text_style: false,
    button_style: false,
  });

  const toggleAccordion = (section: keyof typeof accordionState) => {
    setAccordionState(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <div>
      <HTMLPopUpPreview htmlPopUpTemplateFormData={htmlPopUpTemplateFormData} />
      <div className="w-full mt-4 mb-4 border-b border-gray-200">
        <h3 className="text-muted-dark-purple">Content</h3>
      </div>
      <Accordion
        title="Text and Background Image"
        section="text_and_background_image_content"
        level="h5"
        isOpen={accordionState.text_and_background_image_content}
        onToggle={() => toggleAccordion('text_and_background_image_content')}
      >
        <div className="flex flex-col flex-1">
          <TextInput
            label="Header Text"
            value={htmlPopUpTemplateFormData.headerText}
            placeholderText="Enter Header Text"
            handleChange={(value: string) => {
              setHTMLPopUpTemplateFormData(prev => ({ ...prev, headerText: value }));
            }}
            errorText={'Header Text is required'}
            isDisabled={isEditingDisabled}
          />
        </div>
        <div className="flex flex-col flex-1">
          <TextInput
            label="Sub Text"
            value={htmlPopUpTemplateFormData.subText}
            placeholderText="Enter Sub Text"
            handleChange={(value: string) => {
              setHTMLPopUpTemplateFormData(prev => ({ ...prev, subText: value }));
            }}
            errorText={'Sub Text is required'}
            isDisabled={isEditingDisabled}
          />
        </div>
        <div className="flex flex-col flex-1">
          <TextInput
            label="Background Image Source"
            value={htmlPopUpTemplateFormData.containerBackgroundImageSrc}
            placeholderText="Enter Container Background Image Source"
            handleChange={(value: string) => {
              setHTMLPopUpTemplateFormData(prev => ({ ...prev, containerBackgroundImageSrc: value }));
            }}
            errorText={'Background Image Source is required'}
            isDisabled={isEditingDisabled}
          />
        </div>
      </Accordion>
      <Accordion
        title="Merchant Logo"
        section="merchant_logo_content"
        level="h5"
        isOpen={accordionState.merchant_logo_content}
        onToggle={() => toggleAccordion('merchant_logo_content')}
      >
        <div className="flex flex-col flex-1">
          <TextInput
            label="Merchant Logo"
            value={htmlPopUpTemplateFormData.merchantLogoSrc}
            placeholderText="Merchant Logo Source"
            handleChange={(value: string) => {
              setHTMLPopUpTemplateFormData(prev => ({ ...prev, merchantLogoSrc: value }));
            }}
            errorText={'Merchant Logo is required'}
            isDisabled={isEditingDisabled}
          />
        </div>
        <div className="flex flex-col flex-1">
          <TextInput
            label="Merchant Logo Alt"
            value={htmlPopUpTemplateFormData.merchantLogoAlt}
            placeholderText="Merchant Logo Alt"
            handleChange={(value: string) => {
              setHTMLPopUpTemplateFormData(prev => ({ ...prev, merchantLogoAlt: value }));
            }}
            errorText={'Partner Logo Alt is required'}
            isDisabled={isEditingDisabled}
          />
        </div>
      </Accordion>
      <Accordion
        title="Button"
        section="button_content"
        level="h5"
        isOpen={accordionState.button_content}
        onToggle={() => toggleAccordion('button_content')}
      >
        <div className="flex gap-4">
          <div className="flex flex-col flex-1">
            <TextInput
              label="Button Prefix Text"
              value={htmlPopUpTemplateFormData.buttonPrefixText}
              placeholderText="Enter Button Prefix Text"
              handleChange={(value: string) => {
                setHTMLPopUpTemplateFormData(prev => ({ ...prev, buttonPrefixText: value }));
              }}
              errorText={'Button Prefix Text is required'}
              isDisabled={isEditingDisabled}
            />
          </div>
          <div className="flex flex-col flex-1">
            <TextInput
              label="Button Text Callout"
              value={htmlPopUpTemplateFormData.buttonTextCallout}
              placeholderText="Enter Button Text Callout"
              handleChange={(value: string) => {
                setHTMLPopUpTemplateFormData(prev => ({ ...prev, buttonTextCallout: value }));
              }}
              errorText={'Button Text Callout is required'}
              isDisabled={isEditingDisabled}
            />
          </div>
          <div className="flex flex-col flex-1">
            <TextInput
              label="Button Suffix Text"
              value={htmlPopUpTemplateFormData.buttonSuffixText}
              placeholderText="Enter Button Suffix Text"
              handleChange={(value: string) => {
                setHTMLPopUpTemplateFormData(prev => ({ ...prev, buttonSuffixText: value }));
              }}
              errorText={'Button Suffix Text is required'}
              isDisabled={isEditingDisabled}
            />
          </div>
        </div>

        <div className="flex flex-col flex-1">
          <TextInput
            label="Button Image Src"
            value={htmlPopUpTemplateFormData.buttonImageSrc}
            placeholderText="Enter Button Image Src"
            handleChange={(value: string) => {
              setHTMLPopUpTemplateFormData(prev => ({ ...prev, buttonImageSrc: value }));
            }}
            errorText={'Button Image Src is required'}
            isDisabled={isEditingDisabled}
          />
        </div>
        <div className="flex flex-col flex-1">
          <TextInput
            label="Button Image Alt"
            value={htmlPopUpTemplateFormData.buttonImageAlt}
            placeholderText="Enter Button Image Alt"
            handleChange={(value: string) => {
              setHTMLPopUpTemplateFormData(prev => ({ ...prev, buttonImageAlt: value }));
            }}
            errorText={'Button Image Alt is required'}
            isDisabled={isEditingDisabled}
          />
        </div>
        <div className="flex flex-col flex-1">
          <TextInput
            label="Button Destination URL"
            value={htmlPopUpTemplateFormData.destinationURL}
            placeholderText="Enter Button Destination URL"
            handleChange={(value: string) => {
              setHTMLPopUpTemplateFormData(prev => ({ ...prev, destinationURL: value }));
            }}
            errorText={'Button Destination URL is required'}
            isDisabled={isEditingDisabled}
          />
        </div>
      </Accordion>
      <div className="w-full mt-4 mb-4 border-b border-gray-200">
        <h3 className="text-muted-dark-purple">Styling</h3>
      </div>

      <Accordion
        title="Container & Background Image"
        section="container_background_image_style"
        level="h5"
        isOpen={accordionState.container_background_image_style}
        onToggle={() => toggleAccordion('container_background_image_style')}
      >
        <div className="flex flex-col flex-1">
          <TextInput
            label="Container Width in pixels"
            value={htmlPopUpTemplateFormData.containerWidth}
            placeholderText="Enter Container Width in pixels (e.g., 300)"
            handleChange={(value: string) => {
              setHTMLPopUpTemplateFormData(prev => ({ ...prev, containerWidth: value }));
            }}
            errorText={'Container Width is required'}
            inputType="number"
            helperText={`Output: ${htmlPopUpTemplateFormData.containerWidth}px`}
            isDisabled={isEditingDisabled}
          />
        </div>

        <div className="flex flex-col flex-1">
          <TextInput
            label="Background Image Opacity in percentage"
            value={htmlPopUpTemplateFormData.containerBackgroundColorOpacity}
            placeholderText="Enter Container Background Image Opacity"
            handleChange={(value: string) => {
              setHTMLPopUpTemplateFormData(prev => ({ ...prev, containerBackgroundColorOpacity: value }));
            }}
            errorText={'Background Image Opacity is required'}
            inputType="number"
            inputProps={{ min: '0', max: '100' }}
            helperText={`Output: ${htmlPopUpTemplateFormData.containerBackgroundColorOpacity}% [Higher the value, the darker the background image]`}
            isDisabled={isEditingDisabled}
          />
        </div>
        <div className="flex flex-col flex-1">
          <h6 className="text-muted-dark-purple">Container Background Image Position</h6>
          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <TextInput
                label="X Position in pixels"
                value={htmlPopUpTemplateFormData.containerBackgroundImagePosition.x}
                placeholderText="Enter X Position"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    containerBackgroundImagePosition: { ...prev.containerBackgroundImagePosition, x: value },
                  }));
                }}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Y Position in pixels"
                value={htmlPopUpTemplateFormData.containerBackgroundImagePosition.y}
                placeholderText="Enter Y Position"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    containerBackgroundImagePosition: { ...prev.containerBackgroundImagePosition, y: value },
                  }));
                }}
                isDisabled={isEditingDisabled}
              />
            </div>
          </div>
        </div>
      </Accordion>

      <Accordion
        title="Merchant Logo"
        section="merchant_logo_style"
        level="h5"
        isOpen={accordionState.merchant_logo_style}
        onToggle={() => toggleAccordion('merchant_logo_style')}
      >
        <div className="flex flex-col flex-1">
          <div className="flex flex-col flex-1">
            <TextInput
              label="Logo Width in percentages"
              value={htmlPopUpTemplateFormData.merchantLogoWidth}
              placeholderText="Enter Logo Width"
              handleChange={(value: string) => {
                setHTMLPopUpTemplateFormData(prev => ({ ...prev, merchantLogoWidth: value }));
              }}
              inputType="number"
              inputProps={{ min: '0', max: '100' }}
              helperText={`Output: ${htmlPopUpTemplateFormData.merchantLogoWidth}%`}
              isDisabled={isEditingDisabled}
            />
          </div>
          <h6 className="text-muted-dark-purple">Logo Margin in pixels</h6>
          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <TextInput
                label="Left"
                value={htmlPopUpTemplateFormData.merchantLogoMargin.left}
                placeholderText="Enter Left"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    merchantLogoMargin: { ...prev.merchantLogoMargin, left: value },
                  }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.merchantLogoMargin.left}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Top"
                value={htmlPopUpTemplateFormData.merchantLogoMargin.top}
                placeholderText="Enter Top"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    merchantLogoMargin: { ...prev.merchantLogoMargin, top: value },
                  }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.merchantLogoMargin.top}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Right"
                value={htmlPopUpTemplateFormData.merchantLogoMargin.right}
                placeholderText="Enter Right"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    merchantLogoMargin: { ...prev.merchantLogoMargin, right: value },
                  }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.merchantLogoMargin.right}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Bottom"
                value={htmlPopUpTemplateFormData.merchantLogoMargin.bottom}
                placeholderText="Enter Bottom"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    merchantLogoMargin: { ...prev.merchantLogoMargin, bottom: value },
                  }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.merchantLogoMargin.bottom}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
          </div>
        </div>
      </Accordion>

      <Accordion
        title="Text"
        section="text_style"
        level="h5"
        isOpen={accordionState.text_style}
        onToggle={() => toggleAccordion('text_style')}
      >
        <div className="flex flex-col flex-1">
          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <TextInput
                label="Text Color"
                value={htmlPopUpTemplateFormData.textColor}
                placeholderText="Enter Text Color"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({ ...prev, textColor: value }));
                }}
                helperText={`In Hex Code values: e.g., #fff or #000000`}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Text Font Family"
                value={htmlPopUpTemplateFormData.textFontFamily}
                placeholderText="Enter Text Font Family"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({ ...prev, textFontFamily: value }));
                }}
                helperText={`In Font Family values: e.g., Arial, Helvetica, sans-serif, can contain multiple values separated by commas`}
                isDisabled={isEditingDisabled}
              />
            </div>
          </div>
          <h6 className="text-muted-dark-purple">Text Title Margin in pixels</h6>
          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <TextInput
                label="Left"
                value={htmlPopUpTemplateFormData.textTitleMargin.left}
                placeholderText="Enter Left"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    textTitleMargin: { ...prev.textTitleMargin, left: value },
                  }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.textTitleMargin.left}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Top"
                value={htmlPopUpTemplateFormData.textTitleMargin.top}
                placeholderText="Enter Top"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    textTitleMargin: { ...prev.textTitleMargin, top: value },
                  }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.textTitleMargin.top}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Right"
                value={htmlPopUpTemplateFormData.textTitleMargin.right}
                placeholderText="Enter Right"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    textTitleMargin: { ...prev.textTitleMargin, right: value },
                  }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.textTitleMargin.right}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Bottom"
                value={htmlPopUpTemplateFormData.textTitleMargin.bottom}
                placeholderText="Enter Bottom"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    textTitleMargin: { ...prev.textTitleMargin, bottom: value },
                  }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.textTitleMargin.bottom}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
          </div>
          <h6 className="text-muted-dark-purple">Sub Text Margin</h6>
          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <TextInput
                label="Left"
                value={htmlPopUpTemplateFormData.textMargin.left}
                placeholderText="Enter Left"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({ ...prev, textMargin: { ...prev.textMargin, left: value } }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.textMargin.left}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Top"
                value={htmlPopUpTemplateFormData.textMargin.top}
                placeholderText="Enter Top"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({ ...prev, textMargin: { ...prev.textMargin, top: value } }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.textMargin.top}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Right"
                value={htmlPopUpTemplateFormData.textMargin.right}
                placeholderText="Enter Right"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({ ...prev, textMargin: { ...prev.textMargin, right: value } }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.textMargin.right}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Bottom"
                value={htmlPopUpTemplateFormData.textMargin.bottom}
                placeholderText="Enter Bottom"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    textMargin: { ...prev.textMargin, bottom: value },
                  }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.textMargin.bottom}px`}
                isDisabled={isEditingDisabled}
              />
            </div>
          </div>
        </div>
      </Accordion>

      <Accordion
        title="Button"
        section="button_style"
        level="h5"
        isOpen={accordionState.button_style}
        onToggle={() => toggleAccordion('button_style')}
      >
        <div className="flex flex-col flex-1">
          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <TextInput
                label="Button Width in percentages"
                value={htmlPopUpTemplateFormData.buttonWidth}
                placeholderText="Enter Button Width"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({ ...prev, buttonWidth: value }));
                }}
                inputType="number"
                helperText={`Output: ${htmlPopUpTemplateFormData.buttonWidth}%`}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Callout Color"
                value={htmlPopUpTemplateFormData.buttonSpanCalloutColor}
                placeholderText="Enter Callout Color"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({ ...prev, buttonSpanCalloutColor: value }));
                }}
                helperText={`In Hex Code values: e.g., #fff or #000000`}
                isDisabled={isEditingDisabled}
              />
            </div>
          </div>
          <h6 className="text-muted-dark-purple">Button Padding</h6>
          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <TextInput
                label="Left"
                value={htmlPopUpTemplateFormData.buttonPadding.left}
                placeholderText="Enter Left"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    buttonPadding: { ...prev.buttonPadding, left: value },
                  }));
                }}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Top"
                value={htmlPopUpTemplateFormData.buttonPadding.top}
                placeholderText="Enter Top"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    buttonPadding: { ...prev.buttonPadding, top: value },
                  }));
                }}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Right"
                value={htmlPopUpTemplateFormData.buttonPadding.right}
                placeholderText="Enter Right"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    buttonPadding: { ...prev.buttonPadding, right: value },
                  }));
                }}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Bottom"
                value={htmlPopUpTemplateFormData.buttonPadding.bottom}
                placeholderText="Enter Bottom"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    buttonPadding: { ...prev.buttonPadding, bottom: value },
                  }));
                }}
                isDisabled={isEditingDisabled}
              />
            </div>
          </div>
          <h6 className="text-muted-dark-purple">Button Image</h6>
          <div className="flex flex-col flex-1">
            <TextInput
              label="Width"
              value={htmlPopUpTemplateFormData.buttonImageWidth}
              placeholderText="Enter Button Image Width"
              handleChange={(value: string) => {
                setHTMLPopUpTemplateFormData(prev => ({ ...prev, buttonImageWidth: value }));
              }}
              isDisabled={isEditingDisabled}
            />
          </div>
          <h6 className="text-muted-dark-purple">Button Image Margin</h6>
          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <TextInput
                label="Left"
                value={htmlPopUpTemplateFormData.buttonImageMargin.left}
                placeholderText="Enter Left"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    buttonImageMargin: { ...prev.buttonImageMargin, left: value },
                  }));
                }}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Top"
                value={htmlPopUpTemplateFormData.buttonImageMargin.top}
                placeholderText="Enter Top"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    buttonImageMargin: { ...prev.buttonImageMargin, top: value },
                  }));
                }}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Right"
                value={htmlPopUpTemplateFormData.buttonImageMargin.right}
                placeholderText="Enter Right"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    buttonImageMargin: { ...prev.buttonImageMargin, right: value },
                  }));
                }}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Bottom"
                value={htmlPopUpTemplateFormData.buttonImageMargin.bottom}
                placeholderText="Enter Bottom"
                handleChange={(value: string) => {
                  setHTMLPopUpTemplateFormData(prev => ({
                    ...prev,
                    buttonImageMargin: { ...prev.buttonImageMargin, bottom: value },
                  }));
                }}
                isDisabled={isEditingDisabled}
              />
            </div>
          </div>
        </div>
      </Accordion>
    </div>
  );
};

export default HTMLPopUpTemplateFields;
